import type { BridgeToApiPromptResultMessage } from '../../../types/bridge-to-api-messages.js';
import { augmentPromptResultAuthFields } from '../send-prompt-result-augment.js';
import { clearPlanningSessionTurn } from '../../planning/planning-session-turn-buffer.js';
import { finalizeAndSendPromptResult } from './finalize-and-send-prompt-result.js';
import { prepareAgentPromptText } from './prepare-agent-prompt-text.js';
import { resolveSendPromptImages } from './resolve-send-prompt-images.js';
import type { SendPromptToAgentOptions } from './send-prompt-to-agent-types.js';

export type { SendPromptToAgentOptions } from './send-prompt-to-agent-types.js';

export async function sendPromptToAgent(options: SendPromptToAgentOptions): Promise<void> {
  const {
    handle,
    promptText,
    promptId,
    sessionId,
    runId,
    agentType,
    agentCwd,
    sendResult,
    sendSessionUpdate,
    log,
    followUpCatalogPromptId,
    cloudApiBaseUrl,
    getCloudAccessToken,
    e2ee,
    attachments,
    isNewSession = false,
  } = options;

  let isPlanningSession = false;
  try {
    const prepared = await prepareAgentPromptText({
      promptText,
      sessionId,
      runId,
      isNewSession,
      cloudApiBaseUrl,
      getCloudAccessToken,
    });
    isPlanningSession = prepared.isPlanningSession;

    const imagesResolved = await resolveSendPromptImages({
      attachments,
      sessionId,
      promptId,
      runId,
      agentType,
      followUpCatalogPromptId,
      cloudApiBaseUrl,
      getCloudAccessToken,
      e2ee,
      log,
    });
    if (!imagesResolved.ok) {
      sendResult(imagesResolved.errorResult);
      return;
    }

    const result = await handle.sendPrompt(prepared.agentPromptText, imagesResolved.sendOpts);
    await finalizeAndSendPromptResult({
      result,
      sessionId,
      runId,
      promptId,
      agentType,
      agentCwd,
      isPlanningSession,
      followUpCatalogPromptId,
      cloudApiBaseUrl,
      getCloudAccessToken,
      e2ee,
      sendResult,
      sendSessionUpdate,
      log,
    });
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    log(`[Agent] Send failed: ${errMsg}`);
    sendResult({
      type: 'prompt_result',
      id: promptId,
      ...(sessionId ? { sessionId } : {}),
      ...(runId ? { runId } : {}),
      success: false,
      error: errMsg,
      ...(followUpCatalogPromptId != null && followUpCatalogPromptId !== ''
        ? { followUpCatalogPromptId }
        : {}),
      ...augmentPromptResultAuthFields(agentType, errMsg),
    } satisfies BridgeToApiPromptResultMessage);
  } finally {
    if (runId && isPlanningSession) clearPlanningSessionTurn(runId);
  }
}
